import { useSession, useUser } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useResendEmailVerification, useUpdateUser, useUserDetailQuery } from '@/features/users';
import { normalizeClerkErrorMessage } from '@/pages/auth-error-utils';
import { useResendCooldown } from '@/pages/hooks/useResendCooldown';
import { useNotifications } from '@/providers/notificationHooks';
import { useMutationErrorSnackbar } from '@/shared/hooks';
import type { AuthSession } from '@/auth/types';

const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const verificationResendCooldownSeconds = 30;
type ClerkUser = NonNullable<ReturnType<typeof useUser>['user']>;
type PendingEmailAddress = Awaited<ReturnType<ClerkUser['createEmailAddress']>>;
const normaliseEmail = (value: string): string => value.trim().toLowerCase();

const hasClerkErrorCode = (error: unknown, code: string): boolean => {
  if (typeof error !== 'object' || error === null || !('errors' in error)) {
    return false;
  }
  const errors = (error as { errors?: Array<{ code?: string }> }).errors;
  return Array.isArray(errors) && errors.some((entry) => entry.code === code);
};

const normaliseEmailUpdateError = (message: string): string =>
  message.toLowerCase().includes('email address is taken') ? 'Email already in use.' : message;

export type UserProfileFormState = {
  firstName: string;
  lastName: string;
  email: string;
  emailError: boolean;
  emailHelperText?: string;
  emailVerifiedAt: string | null;
  resendVerificationDisabled: boolean;
  emailReadOnly: boolean;
  verificationDialogOpen: boolean;
  verificationCode: string;
  verificationTargetEmail: string;
  verificationError: string | null;
  verificationSubmitting: boolean;
  verificationSendPending: boolean;
  verificationResendOnCooldown: boolean;
  verificationResendSecondsRemaining: number;
  reverificationDialogOpen: boolean;
  reverificationPassword: string;
  reverificationError: string | null;
  reverificationSubmitting: boolean;
  saveDisabled: boolean;
  isLoading: boolean;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onVerificationCodeChange: (value: string) => void;
  onVerificationCancel: () => void;
  onVerificationSubmit: () => void;
  onVerificationResendCode: () => void;
  onReverificationPasswordChange: (value: string) => void;
  onReverificationCancel: () => void;
  onReverificationSubmit: () => void;
  onSave: () => void;
  onResendVerification: () => void;
};

type UserProfileFormModel = {
  state: UserProfileFormState;
  userDetailError: string | null;
  refreshUser: () => Promise<unknown>;
};

export const useUserProfileForm = (session: AuthSession | null): UserProfileFormModel => {
  const { refreshSession } = useAuth();
  const { user: clerkUser } = useUser();
  const { session: clerkSession } = useSession();
  const userDetail = useUserDetailQuery(session?.userId ?? null);
  const updateUser = useUpdateUser();
  const resendVerification = useResendEmailVerification();
  const mutationError = useMutationErrorSnackbar();
  const { notify } = useNotifications();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationTargetEmail, setVerificationTargetEmail] = useState('');
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [verificationSendPending, setVerificationSendPending] = useState(false);
  const [pendingEmailAddress, setPendingEmailAddress] = useState<PendingEmailAddress | null>(null);
  const [reverificationDialogOpen, setReverificationDialogOpen] = useState(false);
  const [reverificationPassword, setReverificationPassword] = useState('');
  const [reverificationError, setReverificationError] = useState<string | null>(null);
  const [reverificationSubmitting, setReverificationSubmitting] = useState(false);
  const pendingReverificationAction = useRef<(() => Promise<void>) | null>(null);
  const {
    secondsRemaining: verificationResendSecondsRemaining,
    onCooldown: verificationResendOnCooldown,
    startCooldown: startVerificationResendCooldown,
  } = useResendCooldown({ seconds: verificationResendCooldownSeconds, tickMs: 250 });
  const [initialProfile, setInitialProfile] = useState<{
    firstName: string;
    lastName: string;
    email: string;
  }>({ firstName: '', lastName: '', email: '' });

  useEffect(() => {
    if (userDetail.data) {
      setFirstName(userDetail.data.firstName ?? '');
      setLastName(userDetail.data.lastName ?? '');
      setEmail(userDetail.data.email);
      setInitialProfile({
        firstName: userDetail.data.firstName ?? '',
        lastName: userDetail.data.lastName ?? '',
        email: userDetail.data.email,
      });
    }
  }, [userDetail.data]);

  const emailError = email.length > 0 && !emailRegex.test(email);
  const clerkManagedEmail = userDetail.data?.externalAuthProvider === 'clerk';
  const emailReadOnly =
    Boolean(userDetail.data?.externalAuthProvider) &&
    userDetail.data?.externalAuthProvider !== 'clerk';
  const emailVerifiedAt = userDetail.data?.emailVerifiedAt
    ? new Date(userDetail.data.emailVerifiedAt).toISOString()
    : null;
  const emailHelperText = emailReadOnly
    ? 'Email is managed by your sign-in provider'
    : emailError
      ? 'Enter a valid email'
      : undefined;
  const profileDirty =
    firstName !== initialProfile.firstName ||
    lastName !== initialProfile.lastName ||
    email !== initialProfile.email;
  const saveDisabled = updateUser.isPending || !session || !email || emailError || !profileDirty;
  const resendVerificationDisabled = resendVerification.isPending || Boolean(emailVerifiedAt);

  const closeVerificationDialog = (): void => {
    setVerificationDialogOpen(false);
    setVerificationCode('');
    setVerificationError(null);
    setVerificationSubmitting(false);
    setVerificationSendPending(false);
    setVerificationTargetEmail('');
    const orphanedEmailAddress = pendingEmailAddress;
    setPendingEmailAddress(null);
    if (orphanedEmailAddress && orphanedEmailAddress.verification.status !== 'verified') {
      void orphanedEmailAddress.destroy().catch(() => {
        // Ignore cleanup errors for abandoned unverified addresses.
      });
    }
  };

  const openReverificationDialog = (action: () => Promise<void>): void => {
    pendingReverificationAction.current = action;
    setReverificationPassword('');
    setReverificationError(null);
    setReverificationDialogOpen(true);
  };

  const closeReverificationDialog = (): void => {
    pendingReverificationAction.current = null;
    setReverificationDialogOpen(false);
    setReverificationPassword('');
    setReverificationError(null);
    setReverificationSubmitting(false);
  };

  const removePreviousClerkEmailAddress = async (
    previousEmail: string,
    newPrimaryEmailAddressId: string,
  ): Promise<void> => {
    if (!clerkUser) return;
    const previous = clerkUser.emailAddresses.find(
      (entry) =>
        normaliseEmail(entry.emailAddress) === normaliseEmail(previousEmail) &&
        entry.id !== newPrimaryEmailAddressId,
    );
    if (!previous) return;
    if (clerkUser.primaryEmailAddressId === previous.id) return;
    try {
      await previous.destroy();
      await clerkUser.reload();
    } catch {
      notify({
        message: 'Primary email updated, but previous email could not be removed.',
        tone: 'orange',
      });
    }
  };

  const beginEmailCodeVerification = async (
    emailAddress: PendingEmailAddress,
    targetEmail: string,
  ): Promise<void> => {
    const preparedEmailAddress = await emailAddress.prepareVerification({
      strategy: 'email_code',
    });
    setPendingEmailAddress(preparedEmailAddress);
    setVerificationTargetEmail(targetEmail);
    setVerificationCode('');
    setVerificationDialogOpen(true);
    setVerificationError(null);
    startVerificationResendCooldown();
    mutationError.clearError();
    notify({ message: 'Verification code sent. Check your inbox.', tone: 'green' });
  };

  const findExistingEmailAddress = (targetEmail: string): PendingEmailAddress | null => {
    if (!clerkUser) return null;
    const target = normaliseEmail(targetEmail);
    const match = clerkUser.emailAddresses.find(
      (entry) => normaliseEmail(entry.emailAddress) === target,
    );
    return (match as PendingEmailAddress | undefined) ?? null;
  };

  const resumeExistingUnverifiedEmail = async (targetEmail: string): Promise<boolean> => {
    if (!clerkUser) return false;
    await clerkUser.reload();
    const existing = findExistingEmailAddress(targetEmail);
    if (!existing) return false;
    if (existing.verification.status === 'verified') return false;
    await beginEmailCodeVerification(existing, targetEmail);
    return true;
  };

  const saveProfileToApi = (
    input: { id: string; firstName: string; lastName: string; email: string },
    successMessage: string,
  ): void => {
    updateUser.mutate(input, {
      onSuccess: () => {
        mutationError.clearError();
        notify({ message: successMessage, tone: 'green' });
        void Promise.all([refreshSession(), userDetail.refetch()]);
      },
      onError: (err) => {
        mutationError.setError(err);
      },
    });
  };

  const startClerkEmailUpdate = async (nextEmail: string): Promise<void> => {
    if (!clerkManagedEmail || !session) {
      return;
    }
    if (!clerkUser) {
      mutationError.setError(new Error('Auth session is still loading. Please try again.'));
      return;
    }
    setVerificationSendPending(true);
    setVerificationError(null);
    try {
      const existing = findExistingEmailAddress(nextEmail);
      if (existing && existing.verification.status !== 'verified') {
        await beginEmailCodeVerification(existing, nextEmail);
        return;
      }

      const createdEmailAddress = await clerkUser.createEmailAddress({ email: nextEmail });
      await beginEmailCodeVerification(createdEmailAddress, nextEmail);
    } catch (error) {
      if (hasClerkErrorCode(error, 'session_reverification_required')) {
        openReverificationDialog(async () => {
          await startClerkEmailUpdate(nextEmail);
        });
        return;
      }
      if (await resumeExistingUnverifiedEmail(nextEmail)) {
        return;
      }
      if (hasClerkErrorCode(error, 'reverification_cancelled')) {
        mutationError.clearError();
        notify({ message: 'Email update cancelled', tone: 'blue' });
        setVerificationError(null);
        return;
      }
      const message = normalizeClerkErrorMessage(
        error,
        'Unable to start email verification. Please try again.',
      );
      const friendlyMessage = normaliseEmailUpdateError(message);
      mutationError.setError(new Error(friendlyMessage));
      setVerificationError(friendlyMessage);
    } finally {
      setVerificationSendPending(false);
    }
  };

  const handleSaveProfile = (): void => {
    if (!session) return;
    if (emailError) {
      mutationError.setError(new Error('Enter a valid email'));
      return;
    }
    const trimmedEmail = email.trim();
    const emailChanged = trimmedEmail !== initialProfile.email;
    if (emailChanged && clerkManagedEmail) {
      void startClerkEmailUpdate(trimmedEmail);
      return;
    }

    saveProfileToApi(
      { id: session.userId, firstName, lastName, email: trimmedEmail },
      'Profile updated',
    );
  };

  const handleVerificationSubmit = (): void => {
    if (!session || !clerkUser || !pendingEmailAddress) return;
    const code = verificationCode.trim();
    if (!code) {
      setVerificationError('Verification code is required.');
      return;
    }

    const persistProfileAfterVerification = (): void => {
      const namesChanged =
        firstName !== initialProfile.firstName || lastName !== initialProfile.lastName;
      if (!namesChanged) {
        mutationError.clearError();
        notify({ message: 'Email updated', tone: 'green' });
        void Promise.all([refreshSession(), userDetail.refetch()]);
        closeVerificationDialog();
        return;
      }
      updateUser.mutate(
        {
          id: session.userId,
          firstName,
          lastName,
        },
        {
          onSuccess: () => {
            mutationError.clearError();
            notify({ message: 'Profile updated', tone: 'green' });
            void Promise.all([refreshSession(), userDetail.refetch()]);
            closeVerificationDialog();
          },
          onError: (err) => {
            mutationError.setError(err);
          },
        },
      );
    };

    setVerificationSubmitting(true);
    setVerificationError(null);
    void pendingEmailAddress
      .attemptVerification({ code })
      .then(async (attemptedAddress: PendingEmailAddress) => {
        if (attemptedAddress.verification.status !== 'verified') {
          setVerificationError('Verification did not complete. Please try again.');
          return;
        }
        try {
          const previousPrimaryEmail = initialProfile.email;
          await clerkUser.update({ primaryEmailAddressId: attemptedAddress.id });
          await clerkUser.reload();
          if (
            normaliseEmail(previousPrimaryEmail) !== normaliseEmail(attemptedAddress.emailAddress)
          ) {
            await removePreviousClerkEmailAddress(previousPrimaryEmail, attemptedAddress.id);
          }
          persistProfileAfterVerification();
        } catch (error: unknown) {
          if (hasClerkErrorCode(error, 'session_reverification_required')) {
            openReverificationDialog(async () => {
              await clerkUser.update({ primaryEmailAddressId: attemptedAddress.id });
              await clerkUser.reload();
              persistProfileAfterVerification();
            });
            return;
          }
          setVerificationError(
            normalizeClerkErrorMessage(error, 'Unable to update your primary email.'),
          );
        }
      })
      .catch((error: unknown) => {
        setVerificationError(
          normalizeClerkErrorMessage(error, 'Verification failed. Please try again.'),
        );
      })
      .finally(() => {
        setVerificationSubmitting(false);
      });
  };

  const handleReverificationSubmit = (): void => {
    const password = reverificationPassword.trim();
    if (!password) {
      setReverificationError('Password is required.');
      return;
    }
    if (!clerkSession) {
      setReverificationError('Auth session is still loading. Please try again.');
      return;
    }

    setReverificationSubmitting(true);
    setReverificationError(null);
    void clerkSession
      .startVerification({ level: 'first_factor' })
      .then(async () => {
        const verification = await clerkSession.attemptFirstFactorVerification({
          strategy: 'password',
          password,
        });
        if (verification.status !== 'complete') {
          throw new Error('Additional verification is required. Use another sign-in method.');
        }
        const resumeAction = pendingReverificationAction.current;
        closeReverificationDialog();
        if (resumeAction) {
          await resumeAction();
        }
      })
      .catch((error: unknown) => {
        if (hasClerkErrorCode(error, 'reverification_cancelled')) {
          closeReverificationDialog();
          mutationError.clearError();
          notify({ message: 'Email update cancelled', tone: 'blue' });
          return;
        }
        setReverificationError(
          normalizeClerkErrorMessage(error, 'Could not verify your session. Please try again.'),
        );
      })
      .finally(() => {
        setReverificationSubmitting(false);
      });
  };

  const handleVerificationResendCode = (): void => {
    if (!pendingEmailAddress) return;
    if (verificationResendOnCooldown) return;
    setVerificationSendPending(true);
    setVerificationError(null);
    void pendingEmailAddress
      .prepareVerification({ strategy: 'email_code' })
      .then(() => {
        startVerificationResendCooldown();
        notify({ message: 'Verification code sent. Check your inbox.', tone: 'green' });
      })
      .catch((error: unknown) => {
        setVerificationError(
          normalizeClerkErrorMessage(error, 'Unable to resend verification code.'),
        );
      })
      .finally(() => {
        setVerificationSendPending(false);
      });
  };

  const handleResendVerification = (): void => {
    resendVerification.mutate(undefined, {
      onSuccess: (result) => {
        mutationError.clearError();
        if (result.status === 'already-verified') {
          notify({ message: 'Email is already verified', tone: 'green' });
          return;
        }
        notify({ message: 'Verification email sent', tone: 'green' });
        void Promise.all([refreshSession(), userDetail.refetch()]);
      },
      onError: (err) => {
        mutationError.setError(err);
      },
    });
  };

  return {
    state: {
      firstName,
      lastName,
      email,
      emailError,
      emailHelperText,
      emailVerifiedAt,
      resendVerificationDisabled,
      emailReadOnly,
      verificationDialogOpen,
      verificationCode,
      verificationTargetEmail,
      verificationError,
      verificationSubmitting,
      verificationSendPending,
      verificationResendOnCooldown,
      verificationResendSecondsRemaining,
      reverificationDialogOpen,
      reverificationPassword,
      reverificationError,
      reverificationSubmitting,
      saveDisabled,
      isLoading: userDetail.isFetching,
      onFirstNameChange: setFirstName,
      onLastNameChange: setLastName,
      onEmailChange: setEmail,
      onVerificationCodeChange: setVerificationCode,
      onVerificationCancel: closeVerificationDialog,
      onVerificationSubmit: handleVerificationSubmit,
      onVerificationResendCode: handleVerificationResendCode,
      onReverificationPasswordChange: setReverificationPassword,
      onReverificationCancel: closeReverificationDialog,
      onReverificationSubmit: handleReverificationSubmit,
      onSave: handleSaveProfile,
      onResendVerification: handleResendVerification,
    },
    userDetailError: userDetail.error?.message ?? null,
    refreshUser: userDetail.refetch,
  };
};
